import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../data/mock';
import { 
  Home, Key, TrendingUp, Settings, 
  CheckCircle, Phone, ArrowRight 
} from 'lucide-react';

const Services = () => {
  const serviceIcons = {
    'Property Sales': Home,
    'Property Lettings': Key,
    'Investment Advisory': TrendingUp,
    'Property Management': Settings
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-[#E2725B] to-[#FF914D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            OUR SERVICES
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed">
            Comprehensive real estate solutions tailored to your unique needs
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Complete Property Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From buying and selling to investment and management, we provide end-to-end property services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.title];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardHeader className="text-center pb-4">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold" style={{ color: '#2E2E2E' }}>
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-[#FF914D] mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      className="w-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] hover:from-[#d65a43] hover:to-[#e8823d] text-white transition-all duration-300"
                      asChild
                    >
                      <Link to="/contact">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20" style={{ backgroundColor: '#FFF5E6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A streamlined approach designed to deliver exceptional results
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E2725B] text-white flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Initial Consultation
              </h3>
              <p className="text-gray-600">
                We start by understanding your unique needs, preferences, and budget to create a personalized strategy.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E2725B] text-white flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Market Research
              </h3>
              <p className="text-gray-600">
                Our experts conduct thorough market analysis to identify the best opportunities and pricing strategies.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E2725B] text-white flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Execution
              </h3>
              <p className="text-gray-600">
                We implement your personalized plan with precision, keeping you informed every step of the way.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E2725B] text-white flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Completion & Support
              </h3>
              <p className="text-gray-600">
                We ensure smooth completion and provide ongoing support to maintain our long-term relationship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Specialized Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Additional expertise to meet your specific property needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center">
                  <Home className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                  New Build Specialists
                </h3>
                <p className="text-gray-600 mb-6">
                  Expert guidance on London's newest developments and off-plan purchases with exclusive access to pre-launch properties.
                </p>
                <Button variant="outline" asChild>
                  <Link to="/contact">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                  International Clients
                </h3>
                <p className="text-gray-600 mb-6">
                  Specialized services for overseas buyers including visa guidance, currency exchange, and relocation support.
                </p>
                <Button variant="outline" asChild>
                  <Link to="/contact">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center">
                  <Key className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                  Luxury Concierge
                </h3>
                <p className="text-gray-600 mb-6">
                  White-glove service for high-net-worth individuals including private viewings, market intelligence, and exclusive opportunities.
                </p>
                <Button variant="outline" asChild>
                  <Link to="/contact">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Insights */}
      <section className="py-20" style={{ backgroundColor: '#FFF5E6' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-8" style={{ color: '#2E2E2E' }}>
            Market Insights & Reports
          </h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            Stay ahead with our comprehensive market analysis, monthly reports, and exclusive insights into London's property trends. Our research team provides data-driven intelligence to help you make informed decisions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg mb-2" style={{ color: '#E2725B' }}>Monthly Reports</h3>
              <p className="text-gray-600">Detailed market analysis and trends</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg mb-2" style={{ color: '#E2725B' }}>Price Predictions</h3>
              <p className="text-gray-600">AI-powered property valuations</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg mb-2" style={{ color: '#E2725B' }}>Investment Opportunities</h3>
              <p className="text-gray-600">Exclusive off-market deals</p>
            </div>
          </div>
          <Button 
            className="bg-gradient-to-r from-[#E2725B] to-[#FF914D] hover:from-[#d65a43] hover:to-[#e8823d] text-white px-8 py-4 text-lg font-semibold"
            asChild
          >
            <Link to="/contact">Subscribe to Reports</Link>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#E2725B] to-[#FF914D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get A Free Consultation
          </h2>
          <p className="text-xl mb-8">
            Speak with one of our property experts to discuss your needs and discover how we can help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-white text-[#E2725B] hover:bg-gray-100 px-8 py-4 text-lg font-semibold flex items-center justify-center gap-2"
              asChild
            >
              <Link to="/contact">
                Book Consultation
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#E2725B] px-8 py-4 text-lg font-semibold flex items-center justify-center gap-2"
              asChild
            >
              <a href="tel:+442071234567">
                <Phone className="h-5 w-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;